// client/src/types/commonComponents.ts
//
// Typed facades over the shared (still JavaScript) common components and
// contexts the TypeScript screens use — the single source of their prop types.
// Under `allowJs` TypeScript reads their JSDoc, and a `@param {Date|string}
// startDate` written for a destructured props object is applied to the whole
// object; AuthContext is `createContext(null)`, so `useAuth()` reads as never.
// The modules are re-exported here with their real shapes instead. Same
// modules, same runtime; when one is converted to .tsx its facade can go.
//
// Used by the LexiStar M4 screens (Dashboard, KPIs, Dwell, Replay's idling
// heatmap, Reports, trip detail), plan & dispatch (ST-00198), Help & training
// (ST-00200) and the app shell (App, EnhancedSidebar). Was
// Dashboard/commonTyped.ts (ST-00188 M4-D).

import type { AnchorHTMLAttributes, ChangeEvent, ComponentType, ReactNode } from 'react';
import AppLinkJs from '../components/common/AppLink';
import PageHeaderJs from '../components/common/PageHeader';
import ThemedSelectJs from '../components/common/ThemedSelect';
import SegmentedControlJs from '../components/common/SegmentedControl';
import LexiStarMapJs from '../components/map/LexiStarMap';
import { useAuth as useAuthJs } from '../context/AuthContext';
import type { MapLayerMouseEvent, MapProps } from 'react-map-gl/maplibre';
import type { Map as MaplibreMap } from 'maplibre-gl';

/**
 * Shell-safe in-app link (components/common/AppLink.js): a router <Link> untabbed, an anchor
 * that navigates the active tab when the tab shell is on. Works inside and outside the routers.
 */
export interface AppLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  to: string;
  children?: ReactNode;
}
export const AppLink = AppLinkJs as unknown as ComponentType<AppLinkProps>;

export interface PageHeaderProps {
  icon?: ComponentType<{ className?: string }>;
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  toolbar?: ReactNode;
  toolbarAlign?: 'start' | 'end';
  inline?: boolean;
  sticky?: boolean;
  className?: string;
}
export const PageHeader = PageHeaderJs as unknown as ComponentType<PageHeaderProps>;

export interface ThemedSelectProps {
  value: string | string[];
  /** Fires with a synthetic `{ target: { name, value } }`, like a native select. */
  onChange: (e: ChangeEvent<HTMLSelectElement> | { target: { name?: string; value: string } }) => void;
  children: ReactNode;
  id?: string;
  name?: string;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  isClearable?: boolean;
  'aria-label'?: string;
}
export const ThemedSelect = ThemedSelectJs as unknown as ComponentType<ThemedSelectProps>;

/** ThemedSelect with `isMulti`: the value is an array and onChange emits one (ST-00198 plan grid). */
export interface ThemedMultiSelectProps extends Omit<ThemedSelectProps, 'value' | 'onChange'> {
  isMulti: true;
  value: string[];
  onChange: (e: { target: { name?: string; value: string[] } }) => void;
}
export const ThemedMultiSelect = ThemedSelectJs as unknown as ComponentType<ThemedMultiSelectProps>;

// Converted to TypeScript (ST-00204): re-exported with its own props.
export { default as UnifiedDateRangePicker } from '../components/common/UnifiedDateRangePicker';
export type { UnifiedDateRangePickerProps } from '../components/common/UnifiedDateRangePicker';

export interface SegmentOption<V extends string | number> {
  value: V;
  label: ReactNode;
  shortLabel?: ReactNode;
  icon?: ComponentType<{ className?: string }>;
  badge?: ReactNode;
}
export interface SegmentedControlProps<V extends string | number> {
  options: SegmentOption<V>[];
  value: V;
  onChange: (v: V) => void;
  ariaLabel: string;
  className?: string;
}
export const SegmentedControl = SegmentedControlJs as unknown as <V extends string | number>(
  props: SegmentedControlProps<V>
) => ReactNode;

export interface LexiStarMapProps extends Omit<MapProps, 'onLoad' | 'onClick' | 'onMouseMove' | 'onMouseLeave' | 'onMouseEnter' | 'onMoveEnd' | 'children' | 'mapStyle'> {
  initialViewState?: { longitude: number; latitude: number; zoom: number };
  basemap?: 'street' | 'satellite';
  defaultBasemap?: 'street' | 'satellite';
  onBasemapChange?: (b: 'street' | 'satellite') => void;
  showBasemapToggle?: boolean;
  basemapTogglePosition?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  showNavigation?: boolean;
  navigationPosition?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  interactiveLayerIds?: string[];
  onLoad?: (e: { target: MaplibreMap }) => void;
  onClick?: (e: MapLayerMouseEvent) => void;
  onMouseMove?: (e: MapLayerMouseEvent) => void;
  onMouseEnter?: (e: MapLayerMouseEvent) => void;
  onMouseLeave?: (e: MapLayerMouseEvent) => void;
  onMoveEnd?: () => void;
  className?: string;
  overlay?: ReactNode;
  children?: ReactNode;
}
/** The shared LexiStar map (react-map-gl/maplibre wrapper, components/map/LexiStarMap.js). */
export const LexiStarMap = LexiStarMapJs as unknown as ComponentType<LexiStarMapProps>;

/** The slice of AuthContext the typed screens read (context/AuthContext.js). */
export interface LexiAuth {
  hasPermission: (key: string) => boolean;
  tenant?: { tenant_id?: number; tenant_name?: string } | null;
  /** GET /api/auth/me `user` ({ userId, email, username, mustChangePassword }). */
  user?: { userId?: number; username?: string; email?: string } | null;
  /** Tenant brand logo (ST-00127); null/undefined = LexiBridge default branding. */
  tenantLogo?: { dataUri?: string; name?: string | null } | null;
  authenticated?: boolean;
  authEnabled?: boolean;
  /**
   * The server requires a password change before anything else (ST-00215, audit IAM-14):
   * every API behind `authenticate` answers 403 `password_change_required` until it is done.
   */
  mustChangePassword?: boolean;
  /** True while the session is being resolved (the splash stays up). */
  loading?: boolean;
  /** The session check could not reach LexiBridge: show "Can't reach LexiBridge" (ST-00303). */
  connectionProblem?: boolean;
  logout?: () => void;
}
export const useAuth = (): LexiAuth => useAuthJs() as unknown as LexiAuth;

// Appearance (context/ThemeContext.tsx, ST-00127): Detect follows the OS. Typed at the source
// since ST-00138; re-exported here for the screens that already import it from this facade.
export { useTheme } from '../context/ThemeContext';
export type { ThemeMode, ThemeContextValue as ThemeView } from '../context/ThemeContext';
